import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Loader2, Lock, Plus, Trash2, Ticket, LogOut, Layers, Download, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  adminLogin, adminLogout, adminListCoupons, adminCreateCoupon, adminCreateBulkCoupons, adminToggleCoupon, adminDeleteCoupon,
} from "@/lib/api";

const AUD_LABEL = { both: "Gründer + Business", gruender: "Gründer", business: "Business" };
const AUD_BADGE = { both: "bg-slate-100 text-slate-600", gruender: "bg-emerald-100 text-emerald-700", business: "bg-sky-100 text-sky-700" };

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = wird geprüft, true, false
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", max_redemptions: "20", expires_at: "", note: "", discount_type: "free", discount_value: "", audience: "both" });
  const [creating, setCreating] = useState(false);
  const [bulk, setBulk] = useState({ count: "10", prefix: "", expires_at: "", note: "", discount_type: "free", discount_value: "", audience: "both" });
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListCoupons();
      setCoupons(res.coupons || []);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) { setAuthed(false); setCoupons([]); }
      else toast.error("Gutscheine konnten nicht geladen werden.");
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(async () => {
    try { await adminLogout(); } catch { /* Cookie ohnehin invalidieren */ }
    setAuthed(false);
    setCoupons([]);
  }, []);

  // Beim Laden die Sitzung über das httpOnly-Cookie prüfen (401 -> Login anzeigen).
  useEffect(() => { load(); }, [load]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      await adminLogin(password);
      setPassword("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen.");
    } finally { setLoggingIn(false); }
  };

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!form.code.trim()) { toast.warning("Bitte einen Code angeben."); return; }
    if (form.discount_type === "percent") {
      const v = parseFloat(form.discount_value);
      if (!(v >= 1 && v <= 99)) { toast.warning("Prozent-Rabatt muss zwischen 1 und 99 liegen."); return; }
    }
    if (form.discount_type === "fixed") {
      const v = parseFloat(form.discount_value);
      if (!(v > 0)) { toast.warning("Der feste Preis muss größer als 0 € sein."); return; }
    }
    setCreating(true);
    try {
      await adminCreateCoupon({
        code: form.code.trim(),
        max_redemptions: parseInt(form.max_redemptions || "0", 10) || 0,
        expires_at: form.expires_at || null,
        note: form.note.trim(),
        discount_type: form.discount_type,
        discount_value: form.discount_type === "free" ? 0 : parseFloat(form.discount_value || "0") || 0,
        audience: form.audience,
      });
      toast.success(`Code ${form.code.trim().toUpperCase()} erstellt.`);
      setForm({ code: "", max_redemptions: "20", expires_at: "", note: "", discount_type: "free", discount_value: "", audience: "both" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code konnte nicht erstellt werden.");
    } finally { setCreating(false); }
  };

  const handleBulkCreate = async (e) => {
    e?.preventDefault();
    const count = parseInt(bulk.count || "0", 10);
    if (!(count >= 1 && count <= 500)) { toast.warning("Anzahl muss zwischen 1 und 500 liegen."); return; }
    if (bulk.discount_type === "percent") {
      const v = parseFloat(bulk.discount_value);
      if (!(v >= 1 && v <= 99)) { toast.warning("Prozent-Rabatt muss zwischen 1 und 99 liegen."); return; }
    }
    if (bulk.discount_type === "fixed") {
      const v = parseFloat(bulk.discount_value);
      if (!(v > 0)) { toast.warning("Der feste Preis muss größer als 0 € sein."); return; }
    }
    setBulkCreating(true);
    try {
      const res = await adminCreateBulkCoupons({
        count,
        prefix: bulk.prefix.trim(),
        expires_at: bulk.expires_at || null,
        note: bulk.note.trim(),
        discount_type: bulk.discount_type,
        discount_value: bulk.discount_type === "free" ? 0 : parseFloat(bulk.discount_value || "0") || 0,
        audience: bulk.audience,
      });
      setBulkResult(res);
      toast.success(`${res.count} Einmal-Codes erzeugt.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codes konnten nicht erzeugt werden.");
    } finally { setBulkCreating(false); }
  };

  const exportBulkCsv = () => {
    if (!bulkResult?.codes?.length) return;
    const dt = bulkResult.discount_type === "percent" ? `${bulkResult.discount_value}%`
      : bulkResult.discount_type === "fixed" ? `${bulkResult.discount_value} EUR` : "Gratis";
    const header = "Code;Rabatt;Funnel;Ablauf\n";
    const rows = bulkResult.codes.map((c) => `${c};${dt};${AUD_LABEL[bulkResult.audience] || bulkResult.audience};${bulkResult.expires_at || ""}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `gutscheine-${bulkResult.batch}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyBulkCodes = async () => {
    if (!bulkResult?.codes?.length) return;
    try { await navigator.clipboard.writeText(bulkResult.codes.join("\n")); toast.success("Codes kopiert."); }
    catch { toast.error("Kopieren nicht möglich."); }
  };

  const toggle = async (code) => {
    try { await adminToggleCoupon(code); load(); }
    catch { toast.error("Aktion fehlgeschlagen."); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Code ${code} wirklich löschen?`)) return;
    try { await adminDeleteCoupon(code); toast.success("Gelöscht."); load(); }
    catch { toast.error("Löschen fehlgeschlagen."); }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-checking">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Helmet><title>Admin · Gründio</title><meta name="robots" content="noindex" /></Helmet>
        <form onSubmit={handleLogin} data-testid="admin-login-form" className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
          <div className="flex items-center gap-2 text-slate-900 font-bold text-lg mb-1" style={{ fontFamily: "Outfit" }}>
            <Lock className="h-5 w-5 text-emerald-600" /> Gründio Admin
          </div>
          <p className="text-sm text-slate-500 mb-6">Bitte Admin-Passwort eingeben.</p>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passwort"
            className="h-12 rounded-xl mb-4"
            autoFocus
          />
          <Button type="submit" disabled={loggingIn} data-testid="admin-login-btn" className="w-full h-12 rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700 text-white">
            {loggingIn ? <Loader2 className="h-5 w-5 animate-spin" /> : "Anmelden"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-10">
      <Helmet><title>Admin · Gutscheine · Gründio</title><meta name="robots" content="noindex" /></Helmet>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            <Ticket className="h-6 w-6 text-emerald-600" /> Gutscheincodes
          </h1>
          <Button onClick={logout} variant="outline" data-testid="admin-logout-btn" className="rounded-xl border-slate-300">
            <LogOut className="h-4 w-4 mr-2" /> Abmelden
          </Button>
        </div>

        {/* Create */}
        <form onSubmit={handleCreate} data-testid="coupon-create-form" className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm mb-8">
          <div className="text-sm font-semibold text-slate-900 mb-4">Neuen Code anlegen</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-500">Code *</label>
              <Input data-testid="new-coupon-code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="z. B. FEEDBACK2026" className="h-11 rounded-xl uppercase" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Max. Einlösungen</label>
              <Input data-testid="new-coupon-max" type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))} className="h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Ablaufdatum (optional)</label>
              <Input data-testid="new-coupon-expiry" type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} className="h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Notiz (optional)</label>
              <Input data-testid="new-coupon-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="z. B. Beta-Tester" className="h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Rabatt-Typ</label>
              <select
                data-testid="new-coupon-discount-type"
                value={form.discount_type}
                onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value, discount_value: "" }))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="free">Gratis (100 %)</option>
                <option value="percent">Prozent-Rabatt</option>
                <option value="fixed">Fester Preis (€)</option>
              </select>
            </div>
            {form.discount_type !== "free" && (
              <div>
                <label className="text-xs text-slate-500">
                  {form.discount_type === "percent" ? "Rabatt in % (1–99)" : "Fester Preis in € (Endpreis)"}
                </label>
                <Input
                  data-testid="new-coupon-discount-value"
                  type="number"
                  step={form.discount_type === "percent" ? "1" : "0.01"}
                  value={form.discount_value}
                  onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                  placeholder={form.discount_type === "percent" ? "z. B. 20" : "z. B. 19.90"}
                  className="h-11 rounded-xl"
                />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500">Gilt für (Funnel)</label>
              <select
                data-testid="new-coupon-audience"
                value={form.audience}
                onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="both">Gründer + Business</option>
                <option value="gruender">Nur Gründer</option>
                <option value="business">Nur Business</option>
              </select>
            </div>
          </div>
          <Button type="submit" disabled={creating} data-testid="create-coupon-btn" className="mt-4 h-11 rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-700 text-white">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> Code erstellen</>}
          </Button>
        </form>

        {/* Bulk create (single-use campaign codes) */}
        <form onSubmit={handleBulkCreate} data-testid="coupon-bulk-form" className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm mb-8">
          <div className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2"><Layers className="h-4 w-4 text-emerald-600" /> Bulk: Einmal-Codes für eine Kampagne</div>
          <p className="text-xs text-slate-500 mb-4">Erzeugt mehrere zufällige Codes, jeder <b>einmal</b> einlösbar. Danach als CSV exportierbar.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-500">Anzahl (1–500) *</label>
              <Input data-testid="bulk-count" type="number" value={bulk.count} onChange={(e) => setBulk((f) => ({ ...f, count: e.target.value }))} className="h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Präfix (optional)</label>
              <Input data-testid="bulk-prefix" value={bulk.prefix} onChange={(e) => setBulk((f) => ({ ...f, prefix: e.target.value.toUpperCase() }))} placeholder="z. B. MESSE" className="h-11 rounded-xl uppercase" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Rabatt-Typ</label>
              <select data-testid="bulk-discount-type" value={bulk.discount_type} onChange={(e) => setBulk((f) => ({ ...f, discount_type: e.target.value, discount_value: "" }))} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm">
                <option value="free">Gratis (100 %)</option>
                <option value="percent">Prozent-Rabatt</option>
                <option value="fixed">Fester Preis (€)</option>
              </select>
            </div>
            {bulk.discount_type !== "free" && (
              <div>
                <label className="text-xs text-slate-500">{bulk.discount_type === "percent" ? "Rabatt in % (1–99)" : "Fester Preis in € (Endpreis)"}</label>
                <Input data-testid="bulk-discount-value" type="number" step={bulk.discount_type === "percent" ? "1" : "0.01"} value={bulk.discount_value} onChange={(e) => setBulk((f) => ({ ...f, discount_value: e.target.value }))} placeholder={bulk.discount_type === "percent" ? "z. B. 20" : "z. B. 19.90"} className="h-11 rounded-xl" />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500">Gilt für (Funnel)</label>
              <select data-testid="bulk-audience" value={bulk.audience} onChange={(e) => setBulk((f) => ({ ...f, audience: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm">
                <option value="both">Gründer + Business</option>
                <option value="gruender">Nur Gründer</option>
                <option value="business">Nur Business</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Ablaufdatum (optional)</label>
              <Input data-testid="bulk-expiry" type="date" value={bulk.expires_at} onChange={(e) => setBulk((f) => ({ ...f, expires_at: e.target.value }))} className="h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Notiz (optional)</label>
              <Input data-testid="bulk-note" value={bulk.note} onChange={(e) => setBulk((f) => ({ ...f, note: e.target.value }))} placeholder="z. B. Messe Berlin 2026" className="h-11 rounded-xl" />
            </div>
          </div>
          <Button type="submit" disabled={bulkCreating} data-testid="bulk-create-btn" className="mt-4 h-11 rounded-xl font-semibold bg-slate-900 hover:bg-slate-800 text-white">
            {bulkCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Layers className="h-4 w-4 mr-2" /> Codes erzeugen</>}
          </Button>

          {bulkResult?.codes?.length > 0 && (
            <div data-testid="bulk-result" className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="text-sm font-semibold text-slate-900">{bulkResult.count} Codes erzeugt · Batch <span className="font-mono">{bulkResult.batch}</span></div>
                <div className="flex items-center gap-2">
                  <Button type="button" onClick={copyBulkCodes} data-testid="bulk-copy-btn" variant="outline" className="h-9 rounded-lg text-xs border-slate-300"><Copy className="h-3.5 w-3.5 mr-1.5" /> Kopieren</Button>
                  <Button type="button" onClick={exportBulkCsv} data-testid="bulk-export-btn" className="h-9 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700 text-white"><Download className="h-3.5 w-3.5 mr-1.5" /> Als CSV exportieren</Button>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5" data-testid="bulk-codes-list">
                {bulkResult.codes.map((c) => (
                  <span key={c} className="font-mono text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-700">{c}</span>
                ))}
              </div>
            </div>
          )}
        </form>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 text-sm font-semibold text-slate-900 flex items-center justify-between">
            <span>Alle Codes</span>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
          {coupons.length === 0 && !loading ? (
            <div className="px-6 py-10 text-center text-sm text-slate-400" data-testid="coupons-empty">Noch keine Codes angelegt.</div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="coupons-list">
              {coupons.map((c) => {
                const exhausted = c.max_redemptions && c.redemptions_used >= c.max_redemptions;
                return (
                  <div key={c.code} data-testid={`coupon-row-${c.code}`} className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-900">{c.code}</span>
                        {(() => {
                          const dt = c.discount_type || "free";
                          const label = dt === "percent" ? `−${c.discount_value}%`
                            : dt === "fixed" ? `${Number(c.discount_value).toFixed(2)} €`
                            : "Gratis";
                          const cls = dt === "free" ? "bg-emerald-100 text-emerald-700"
                            : dt === "percent" ? "bg-amber-100 text-amber-700"
                            : "bg-sky-100 text-sky-700";
                          return <span data-testid={`coupon-discount-${c.code}`} className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>;
                        })()}
                        <span data-testid={`coupon-audience-${c.code}`} className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${AUD_BADGE[c.audience || "both"]}`}>{AUD_LABEL[c.audience || "both"]}</span>
                        {c.max_redemptions === 1 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">Einmal-Code</span>}
                        {c.active
                          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">aktiv</span>
                          : <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">deaktiviert</span>}
                        {exhausted && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold">aufgebraucht</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {c.redemptions_used} / {c.max_redemptions || "∞"} eingelöst
                        {c.expires_at ? ` · läuft ab am ${c.expires_at}` : ""}
                        {c.batch ? ` · Batch ${c.batch}` : ""}
                        {c.note ? ` · ${c.note}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button onClick={() => toggle(c.code)} variant="outline" data-testid={`toggle-${c.code}`} className="h-9 rounded-lg text-xs border-slate-300">
                        {c.active ? "Deaktivieren" : "Aktivieren"}
                      </Button>
                      <Button onClick={() => remove(c.code)} variant="outline" data-testid={`delete-${c.code}`} className="h-9 w-9 p-0 rounded-lg border-slate-300 text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
